import { useState } from "react";
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  TrendingUp,
  Check,
  Activity,
  Layers,
  FileText,
} from "lucide-react";
import { cn } from "@/shared/lib/cn";
import type { SemanticTone } from "@/shared/ui/core/types";

export interface UnifiedColorGroup {
  id: string;
  name: string;
  ruleTitle: string;
  badgeTone: SemanticTone;
  paletteClass: {
    bg: string;
    border: string;
    text: string;
    badgeBg: string;
    badgeText: string;
    stepperRing: string;
    hexOrOklch: string;
  };
  description: string;
  keywords: string[];
  kpiDemo: {
    label: string;
    value: string;
    hint: string;
    badge: string;
    selected?: boolean;
  };
  stepperDemo: {
    title: string;
    subtitle: string;
    state: "pending" | "running" | "completed" | "warning" | "danger";
  };
  timelineDemo: {
    action: string;
    time: string;
    user: string;
    badgeText: string;
  };
  sampleBadges: string[];
}

export function TestUiUnifiedStatusMatrix() {
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string>("all");
  const [cardStyleMode, setCardStyleMode] = useState<"solid" | "soft" | "compare">("solid");

  const colorGroups: UnifiedColorGroup[] = [
    {
      id: "total",
      name: "1. TỔNG SỐ / TẤT CẢ (Màu Trung Tính Web Dashboard)",
      ruleTitle:
        "Bảng màu trung tính Web Dashboard: Nền trắng/slate-50, viền slate-200, tiêu đề slate-500, số liệu slate-900, icon sky-600",
      badgeTone: "neutral",
      paletteClass: {
        bg: "bg-white border-slate-200 text-slate-900 hover:bg-slate-50",
        border: "border-slate-200",
        text: "text-slate-900",
        badgeBg: "bg-slate-100",
        badgeText: "text-slate-700 border-slate-200",
        stepperRing: "bg-white border-slate-300 text-sky-600 ring-2 ring-slate-200",
        hexOrOklch:
          "Web Dashboard Neutral (Nền #FFFFFF / Viền #E2E8F0 / Chữ #64748B / Số #0F172A / Icon #0284C7)",
      },
      description:
        "Bảng màu trung tính chuẩn Web Dashboard theo bảng tổng hợp: Nền card trắng (#FFFFFF hoặc slate-50), viền mảnh slate-200 (#E2E8F0), chữ tiêu đề phụ slate-500 (#64748B), số liệu chính đen/xám đậm tối đa slate-900 (#0F172A), icon điểm nhấn xanh biển sky-600 (#0284C7). Tạo không gian sạch sẽ, nổi bật số liệu và hiện đại.",
      keywords: ["all", "total", "tất cả", "tổng", "tổng số", "tổng cộng", "toàn bộ"],
      kpiDemo: {
        label: "Tổng lệnh sản xuất (All)",
        value: "148",
        hint: "Toàn bộ dữ liệu hệ thống",
        badge: "Tất cả",
      },
      stepperDemo: {
        title: "Toàn bộ quy trình",
        subtitle: "Tổng cộng 5 công đoạn",
        state: "completed",
      },
      timelineDemo: {
        action: "Khởi tạo chu kỳ kế hoạch tổng",
        time: "06:00 - Sáng nay",
        user: "Ban Giám Đốc (MES Lead)",
        badgeText: "Tổng thể",
      },
      sampleBadges: ["All (148)", "Total (148)", "Tất cả", "Tổng số lệnh", "Toàn bộ"],
    },
    {
      id: "draft",
      name: "2. NHÁP & CHỜ (Nền Xám Nhạt - Đúng Theo Hình Mẫu)",
      ruleTitle: "Màu nền nhạt của màu xám (Light Gray) CHỈ DÙNG CHO: draft, nháp, pending, chờ duyệt, chờ sx, tạm lưu",
      badgeTone: "draft",
      paletteClass: {
        bg: "bg-slate-100 border-slate-300 text-slate-700 hover:border-slate-400",
        border: "border-slate-300",
        text: "text-slate-700",
        badgeBg: "bg-slate-100",
        badgeText: "text-slate-700 border-slate-300",
        stepperRing: "bg-surface border-slate-300 text-slate-500 ring-2 ring-slate-200",
        hexOrOklch: "Light Gray (bg-slate-100 / border-slate-300 / text-slate-700)",
      },
      description:
        "Tách riêng biệt thành nhóm màu nền nhạt của màu xám (Light Gray: bg-slate-100 / text-slate-700 / border-slate-300) đúng theo hình ảnh mẫu bạn cung cấp. Áp dụng cho: draft, nháp, pending, chờ duyệt, chờ sx, tạm lưu. Trạng thái khởi tạo hoặc chờ xử lý nhẹ nhàng, không lấn át mốc đang chạy hay hoàn tất.",
      keywords: ["draft", "nháp", "pending", "chờ duyệt", "chờ sx", "tạm lưu", "chờ xử lý", "waiting", "submitted"],
      kpiDemo: {
        label: "Nháp & Chờ duyệt",
        value: "12",
        hint: "Bản nháp & Chờ phân bổ NVL",
        badge: "Chờ duyệt",
      },
      stepperDemo: {
        title: "Bước 1: Lập kế hoạch",
        subtitle: "Đang lưu nháp phòng KHSX",
        state: "pending",
      },
      timelineDemo: {
        action: "Lưu tạm đơn hàng nháp",
        time: "07:30 - Sáng nay",
        user: "Trần Văn A (Kế hoạch)",
        badgeText: "Tạm lưu",
      },
      sampleBadges: ["draft", "nháp", "pending", "chờ duyệt", "chờ sx", "tạm lưu"],
    },
    {
      id: "running",
      name: "3. ĐANG CHẠY (Màu Xanh Dương / Industrial Blue)",
      ruleTitle: "Đang sản xuất, In-progress, Running, Đang xử lý, WIP",
      badgeTone: "info",
      paletteClass: {
        bg: "bg-info border-info-fg/30 text-white",
        border: "border-info/40",
        text: "text-white",
        badgeBg: "bg-info",
        badgeText: "text-white border-info-fg/30",
        stepperRing: "bg-info border-info text-white ring-4 ring-info/25 shadow-xs",
        hexOrOklch: "Info Blue (oklch 54% 0.12 245 / text-white)",
      },
      description:
        "Biểu thị tiến trình công việc thực tế đang diễn ra trong nhà máy (máy đang chạy, xưởng đang chế biến, xe đang vận chuyển). Luôn kèm vòng hào quang và hiệu ứng quay nhẹ (Loader) để người vận hành nhận biết tức thì.",
      keywords: ["inProgress", "running", "đang chạy", "đang sx", "đang sản xuất", "wip", "đang xử lý", "operating"],
      kpiDemo: {
        label: "Đang sản xuất (Running)",
        value: "36",
        hint: "4 dây chuyền đang chạy máy",
        badge: "Đang chạy",
      },
      stepperDemo: {
        title: "Bước 2: Cấp đông & Chế biến",
        subtitle: "Máy IQF-01 đang hoạt động",
        state: "running",
      },
      timelineDemo: {
        action: "Vận hành máy cấp đông IQF",
        time: "09:15 - Hiện tại",
        user: "Nguyễn Văn B (Vận hành)",
        badgeText: "Đang chạy",
      },
      sampleBadges: ["Đang sản xuất (36)", "In-progress", "Đang xử lý", "Đang chạy máy", "WIP Tiến độ 65%"],
    },
    {
      id: "completed",
      name: "4. HOÀN TẤT & ĐẠT (Màu Xanh Primary / Brand Success)",
      ruleTitle: "Hoàn tất, Hoàn thành, Đạt chuẩn KCS, Đã duyệt, Thành công",
      badgeTone: "primary",
      paletteClass: {
        bg: "bg-primary border-emerald-700 text-white",
        border: "border-emerald-700",
        text: "text-white",
        badgeBg: "bg-primary",
        badgeText: "text-white border-emerald-700",
        stepperRing: "bg-primary border-primary text-on-primary ring-2 ring-primary/30",
        hexOrOklch: "Primary Green (#166534 / text-white)",
      },
      description:
        "Dành cho các mốc đã hoàn thành 100%, sản phẩm đạt chuẩn chất lượng QC, phiếu xuất kho đã được phê duyệt, lệnh hoàn tất nhập kho an toàn. Màu thương hiệu chủ đạo của TM Food.",
      keywords: ["completed", "done", "hoàn tất", "hoàn thành", "passed", "đạt", "đã duyệt", "confirmed", "thành công"],
      kpiDemo: {
        label: "Hoàn tất xuất xưởng",
        value: "98",
        hint: "100% đạt chứng chỉ HACCP",
        badge: "Đạt chuẩn",
      },
      stepperDemo: {
        title: "Bước 3: Đóng gói & Hoàn tất",
        subtitle: "Đã dán tem nhãn truy xuất",
        state: "completed",
      },
      timelineDemo: {
        action: "KCS kiểm tra đạt tiêu chuẩn ISO 22000",
        time: "10:30 - Hôm nay",
        user: "Lê Thị C (QC Manager)",
        badgeText: "Hoàn tất",
      },
      sampleBadges: ["Hoàn tất (98)", "Đạt chuẩn (Passed)", "Đã duyệt (Confirmed)", "Đã nhập kho", "Thành công"],
    },
    {
      id: "warning",
      name: "5. CẢNH BÁO & LƯU Ý (Màu Cam Warning)",
      ruleTitle: "Cảnh báo, Sắp hết hạn, Tồn kho thấp, Tạm dừng, Cần lưu ý",
      badgeTone: "warning",
      paletteClass: {
        bg: "bg-warning border-warning-fg/30 text-white",
        border: "border-warning/40",
        text: "text-white",
        badgeBg: "bg-warning",
        badgeText: "text-white border-warning-fg/30",
        stepperRing: "bg-warning border-warning text-white ring-2 ring-warning/30",
        hexOrOklch: "Warning Warm Orange (oklch 61% 0.115 58 / text-white)",
      },
      description:
        "Thông báo các điểm bất thường mức độ trung bình: lô nguyên liệu còn 7 ngày hết hạn, kho đệm gần đầy 90%, máy tạm dừng chờ bảo dưỡng hoặc phiếu cần kiểm tra lại.",
      keywords: ["warning", "cảnh báo", "sắp hết hạn", "gần hạn", "tồn thấp", "tạm dừng", "paused", "at risk", "lưu ý"],
      kpiDemo: {
        label: "Cảnh báo tồn & Gần hạn",
        value: "7",
        hint: "Cần xuất kho trước 48h",
        badge: "Cảnh báo",
      },
      stepperDemo: {
        title: "Cảnh báo nhiệt độ kho",
        subtitle: "Nhiệt độ tiệm cận -17.5°C",
        state: "warning",
      },
      timelineDemo: {
        action: "Phát hiện nhiệt độ tăng nhẹ kho đệm",
        time: "11:00 - Hôm nay",
        user: "Cảm biến Sensor IoT",
        badgeText: "Cảnh báo",
      },
      sampleBadges: ["Cảnh báo (7)", "Sắp hết hạn", "Tồn kho thấp", "Tạm dừng máy", "Chờ kiểm tra lại"],
    },
    {
      id: "danger",
      name: "6. HỦY, KHÔNG ĐẠT, HOLD (Màu Đỏ Danger / Hold)",
      ruleTitle: "Hủy, Đã hủy, Bị từ chối, Không hoàn thành, Tạm giữ HOLD, Vi phạm CCP",
      badgeTone: "danger",
      paletteClass: {
        bg: "bg-danger border-danger-fg/30 text-white",
        border: "border-danger/40",
        text: "text-white",
        badgeBg: "bg-danger",
        badgeText: "text-white border-danger-fg/30",
        stepperRing: "bg-danger border-danger text-white ring-2 ring-danger/30",
        hexOrOklch: "Danger Red (oklch 53% 0.135 27 / text-white)",
      },
      description:
        "Trạng thái nghiêm trọng gây gián đoạn hoặc tổn thất: lệnh bị hủy bỏ, sản phẩm vi phạm kiểm soát tới hạn CCP, lô hàng bị niêm phong tạm giữ kiểm dịch, KCS đánh rớt không đạt.",
      keywords: ["cancel", "cancelled", "đã hủy", "hủy", "từ chối", "hold", "tạm giữ", "vi phạm ccp", "failed", "không đạt"],
      kpiDemo: {
        label: "Hủy / Tạm giữ HOLD",
        value: "4",
        hint: "3 lệnh hủy, 1 lô giữ CCP",
        badge: "Đã hủy / HOLD",
      },
      stepperDemo: {
        title: "Tạm dừng niêm phong CCP",
        subtitle: "Đang lấy mẫu xét nghiệm vi sinh",
        state: "danger",
      },
      timelineDemo: {
        action: "Khóa lô hàng vi phạm kiểm soát CCP-2",
        time: "11:20 - Hôm nay",
        user: "Phạm Văn D (HACCP Lead)",
        badgeText: "Niêm phong",
      },
      sampleBadges: ["Đã hủy (3)", "Tạm giữ (HOLD)", "Không đạt (Failed)", "Vi phạm CCP", "Từ chối duyệt"],
    },
  ];

  const filteredGroups =
    selectedGroupFilter === "all"
      ? colorGroups
      : colorGroups.filter((g) => g.id === selectedGroupFilter);

  return (
    <div className="space-y-6">
      {/* Banner Giới Thiệu & Thống Nhất Quy Chuẩn */}
      <div className="rounded-control border-2 border-primary/30 bg-primary-soft/15 p-5 sm:p-6 shadow-xs relative overflow-hidden space-y-4">
        <div className="flex items-start gap-3.5">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-primary text-on-primary shadow-xs mt-0.5">
            <Sparkles className="size-5" />
          </span>
          <div className="space-y-1 min-w-0 flex-1">
            <h2 className="m-0 text-h2 font-bold text-text tracking-tight">
              Bảng Thống Nhất Màu Sắc Toàn Hệ Thống (Master Color Matrix)
            </h2>
            <p className="m-0 text-body text-text-secondary leading-relaxed">
              Theo đúng chỉ đạo của bạn: <strong className="text-text font-semibold">Tách riêng 2 nhóm màu xám</strong>:
              <br />
              • <strong className="text-slate-800 font-bold bg-slate-200 px-1.5 py-0.5 rounded">Nhóm 1 (TỔNG)</strong>: All, total, tất cả, tổng → Giữ màu xám đậm như cũ (<code className="text-xs bg-slate-700 text-white px-1.5 py-0.2 rounded font-mono">Slate 700 / Text trắng</code>).
              <br />
              • <strong className="text-slate-800 font-bold bg-slate-200 px-1.5 py-0.5 rounded">Nhóm 2 (NHÁP & CHỜ)</strong>: draft, nháp, pending, chờ duyệt, chờ sx, tạm lưu → Màu nền nhạt của màu xám (<code className="text-xs bg-slate-100 text-slate-700 border border-slate-300 px-1.5 py-0.2 rounded font-mono">Slate 100 / Text Slate 700</code>) đúng theo hình ảnh mẫu.
            </p>
          </div>
        </div>

        {/* Thanh Lọc Nhanh 6 Nhóm Màu */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-primary/20">
          <span className="text-caption font-bold text-text-secondary mr-1">Bộ lọc xem nhanh:</span>
          <button
            type="button"
            onClick={() => setSelectedGroupFilter("all")}
            className={cn(
              "h-8 px-3 rounded-md text-caption font-semibold transition-all cursor-pointer",
              selectedGroupFilter === "all"
                ? "bg-primary text-on-primary shadow-xs"
                : "bg-surface border border-border text-text hover:bg-bg"
            )}
          >
            Tất cả 6 nhóm
          </button>
          {colorGroups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSelectedGroupFilter(g.id)}
              className={cn(
                "h-8 px-2.5 rounded-md text-caption font-medium transition-all cursor-pointer border",
                selectedGroupFilter === g.id
                  ? "bg-surface border-primary text-primary font-bold shadow-2xs"
                  : "bg-surface border-border text-text-secondary hover:text-text hover:border-border-strong"
              )}
            >
              {g.id === "total" && "1. Xám Đậm (Tổng)"}
              {g.id === "draft" && "2. Xám Nhạt (Nháp & Chờ)"}
              {g.id === "running" && "3. Xanh Dương (Đang chạy)"}
              {g.id === "completed" && "4. Xanh Lá (Hoàn tất)"}
              {g.id === "warning" && "5. Cam (Cảnh báo)"}
              {g.id === "danger" && "6. Đỏ (Hủy / HOLD)"}
            </button>
          ))}
        </div>
      </div>

      {/* 1. MÔ PHỎNG DẢI CARD KPI THỰC TẾ TRÊN MÀN HÌNH DANH SÁCH (LIVE KPI BAR) */}
      <div className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-border/70 pb-3">
          <div>
            <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
              <TrendingUp className="size-5 text-primary" />
              <span>Mô Phỏng Thực Tế: Dải Thẻ KPI Chuẩn Hóa 6 Nhóm Màu</span>
            </h3>
            <p className="m-0 text-caption text-text-muted mt-0.5">
              Phân định trực quan rạch ròi giữa [Tổng - Xám đậm] và [Nháp & Chờ - Xám nhạt] cùng các mốc màu rực rỡ
            </p>
          </div>

          {/* Toggle Chế Độ Màu Sắc Card KPI */}
          <div className="flex flex-wrap items-center gap-1.5 bg-bg p-1 rounded-control border border-border shrink-0">
            <span className="text-[11px] font-bold text-text-muted px-2">Kiểu thẻ KPI:</span>
            <button
              type="button"
              onClick={() => setCardStyleMode("solid")}
              className={cn(
                "h-7 px-3 rounded text-caption font-semibold transition-all cursor-pointer flex items-center gap-1.5",
                cardStyleMode === "solid"
                  ? "bg-primary text-white shadow-xs"
                  : "text-text-secondary hover:text-text hover:bg-surface"
              )}
            >
              <span>🔥 Màu Gốc Đậm (Chữ Trắng)</span>
              <span className="text-[10px] bg-white/20 px-1 py-0.2 rounded font-bold">Chuẩn</span>
            </button>
            <button
              type="button"
              onClick={() => setCardStyleMode("soft")}
              className={cn(
                "h-7 px-3 rounded text-caption font-semibold transition-all cursor-pointer",
                cardStyleMode === "soft"
                  ? "bg-primary text-white shadow-xs"
                  : "text-text-secondary hover:text-text hover:bg-surface"
              )}
            >
              🍃 Nền Nhạt (Soft %)
            </button>
            <button
              type="button"
              onClick={() => setCardStyleMode("compare")}
              className={cn(
                "h-7 px-3 rounded text-caption font-semibold transition-all cursor-pointer",
                cardStyleMode === "compare"
                  ? "bg-primary text-white shadow-xs"
                  : "text-text-secondary hover:text-text hover:bg-surface"
              )}
            >
              ⚖️ So Sánh Song Song 2 Kiểu
            </button>
          </div>
        </div>

        {/* 1A. Chế độ Solid (Màu Gốc + Text Trắng) */}
        {(cardStyleMode === "solid" || cardStyleMode === "compare") && (
          <div className="space-y-2">
            {cardStyleMode === "compare" && (
              <div className="flex items-center gap-2 text-caption font-bold text-primary">
                <span className="size-2 rounded-full bg-primary" />
                <span>PHONG CÁCH 1: MÀU GỐC ĐẬM (SOLID) — 2 NHÓM MÀU XÁM TÁCH BIỆT RÕ RÀNG</span>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Card 1: Neutral Total - Slate 700 Dark Gray */}
              <div className="relative rounded-control border-2 border-slate-600 bg-slate-700 text-white p-3.5 flex flex-col justify-between shadow-xs transition-transform hover:-translate-y-0.5">
                <div className="flex items-center justify-between text-caption text-white/80">
                  <span className="font-semibold text-white truncate">Tổng lệnh (All/Total)</span>
                  <Layers className="size-3.5 text-white/90 shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-white">148</span>
                  <span className="text-caption text-white/80">lệnh</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-600 text-[11px]">
                  <span className="text-white/80 truncate">Tất cả phân hệ</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/20 text-white font-bold border border-white/30 backdrop-blur-xs">
                    Xám Đậm Cũ
                  </span>
                </div>
              </div>

              {/* Card 2: Draft / Pending - Light Gray Slate 100 */}
              <div className="relative rounded-control border-2 border-slate-300 bg-slate-100 text-slate-800 p-3.5 flex flex-col justify-between shadow-xs transition-transform hover:-translate-y-0.5 ring-2 ring-slate-400/20">
                <div className="flex items-center justify-between text-caption text-slate-600">
                  <span className="font-semibold text-slate-700 truncate">Nháp & Chờ duyệt</span>
                  <FileText className="size-3.5 text-slate-600 shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-slate-900">12</span>
                  <span className="text-caption text-slate-500">chờ xử lý</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200 text-[11px]">
                  <span className="text-slate-600 truncate">draft, pending...</span>
                  <span className="px-1.5 py-0.5 rounded bg-white text-slate-700 font-semibold border border-slate-300 shadow-2xs">
                    Nền Xám Nhạt
                  </span>
                </div>
              </div>

              {/* Card 3: Running - Blue Solid */}
              <div className="relative rounded-control border-2 border-blue-500 bg-blue-600 text-white p-3.5 flex flex-col justify-between shadow-xs ring-2 ring-blue-400/30 transition-transform hover:-translate-y-0.5">
                <div className="flex items-center justify-between text-caption text-white/90">
                  <span className="font-semibold text-white truncate">Đang sản xuất</span>
                  <Loader2 className="size-3.5 text-white animate-spin shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-white">36</span>
                  <span className="text-caption text-white/90">đang chạy</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-blue-500/80 text-[11px]">
                  <span className="text-white/90 truncate">4 chuyền máy</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/20 text-white font-bold border border-white/30 backdrop-blur-xs">
                    Xanh Dương Gốc
                  </span>
                </div>
              </div>

              {/* Card 4: Completed - Green Primary Solid */}
              <div className="relative rounded-control border-2 border-emerald-700 bg-primary text-white p-3.5 flex flex-col justify-between shadow-xs ring-2 ring-primary/40 transition-transform hover:-translate-y-0.5">
                <div className="flex items-center justify-between text-caption text-white/90">
                  <span className="font-semibold text-white truncate">Hoàn tất xuất xưởng</span>
                  <CheckCircle2 className="size-3.5 text-white shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-white">98</span>
                  <span className="text-caption text-white/90">đã xong</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-white/20 text-[11px]">
                  <span className="text-white/90 truncate">100% đạt QC</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/20 text-white font-bold border border-white/30 backdrop-blur-xs">
                    Primary Gốc
                  </span>
                </div>
              </div>

              {/* Card 5: Warning - Orange Solid */}
              <div className="relative rounded-control border-2 border-warning-fg/30 bg-warning text-white p-3.5 flex flex-col justify-between shadow-xs ring-2 ring-warning/30 transition-transform hover:-translate-y-0.5">
                <div className="flex items-center justify-between text-caption text-white/90">
                  <span className="font-semibold text-white truncate">Cảnh báo / Tồn thấp</span>
                  <AlertTriangle className="size-3.5 text-white shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-white">7</span>
                  <span className="text-caption text-white/90">cần xử lý</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-white/20 text-[11px]">
                  <span className="text-white/90 truncate">Hạn &lt; 48h</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/20 text-white font-bold border border-white/30 backdrop-blur-xs">
                    Cam Warning Dịu Mắt
                  </span>
                </div>
              </div>

              {/* Card 6: Danger - Red Solid */}
              <div className="relative rounded-control border-2 border-red-500 bg-red-600 text-white p-3.5 flex flex-col justify-between shadow-xs ring-2 ring-red-400/30 transition-transform hover:-translate-y-0.5">
                <div className="flex items-center justify-between text-caption text-white/90">
                  <span className="font-semibold text-white truncate">Đã hủy / Khóa HOLD</span>
                  <XCircle className="size-3.5 text-white shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-black text-white">4</span>
                  <span className="text-caption text-white/90">hủy/giữ</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-white/20 text-[11px]">
                  <span className="text-white/90 truncate">3 hủy, 1 giữ</span>
                  <span className="px-1.5 py-0.5 rounded bg-white/20 text-white font-bold border border-white/30 backdrop-blur-xs">
                    Đỏ Danger Gốc
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1B. Chế độ Soft (%) */}
        {(cardStyleMode === "soft" || cardStyleMode === "compare") && (
          <div className="space-y-2 pt-2">
            {cardStyleMode === "compare" && (
              <div className="flex items-center gap-2 text-caption font-bold text-text-muted pt-2 border-t border-border/70">
                <span className="size-2 rounded-full bg-border-strong" />
                <span>PHONG CÁCH 2: MÀU NỀN NHẠT (SOFT PASTEL %) — VIỀN MÀU & CHỮ MÀU (KIỂU CŨ)</span>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {/* Card 1: Neutral Total Soft */}
              <div className="relative rounded-control border-2 border-border/80 bg-bg/60 p-3.5 flex flex-col justify-between shadow-2xs">
                <div className="flex items-center justify-between text-caption text-text-muted">
                  <span className="font-semibold text-text-secondary truncate">Tổng lệnh (All)</span>
                  <span className="size-2 rounded-full bg-text-secondary shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-text">148</span>
                  <span className="text-caption text-text-muted">lệnh</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-border/50 text-[11px]">
                  <span className="text-text-muted truncate">Toàn bộ</span>
                  <span className="px-1.5 py-0.5 rounded bg-border/70 text-text-secondary font-semibold">
                    Total Soft
                  </span>
                </div>
              </div>

              {/* Card 2: Draft Soft */}
              <div className="relative rounded-control border-2 border-slate-300 bg-slate-100/70 p-3.5 flex flex-col justify-between shadow-2xs">
                <div className="flex items-center justify-between text-caption text-slate-600">
                  <span className="font-semibold text-slate-700 truncate">Nháp & Chờ</span>
                  <span className="size-2 rounded-full bg-slate-400 shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-slate-800">12</span>
                  <span className="text-caption text-slate-500">bản</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-slate-200 text-[11px]">
                  <span className="text-slate-500 truncate">Chờ xử lý</span>
                  <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-semibold">
                    Draft Soft
                  </span>
                </div>
              </div>

              {/* Card 3: Running - Blue Soft */}
              <div className="relative rounded-control border-2 border-blue-300 bg-blue-50/70 p-3.5 flex flex-col justify-between shadow-2xs ring-1 ring-blue-500/10">
                <div className="flex items-center justify-between text-caption text-blue-700">
                  <span className="font-semibold text-blue-800 truncate">Đang sản xuất</span>
                  <Loader2 className="size-3.5 text-blue-600 animate-spin shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-blue-700">36</span>
                  <span className="text-caption text-blue-600">đang chạy</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-blue-200/80 text-[11px]">
                  <span className="text-blue-700/80 truncate">4 chuyền máy</span>
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold border border-blue-200">
                    Xanh Dương Soft
                  </span>
                </div>
              </div>

              {/* Card 4: Completed - Green Primary Soft */}
              <div className="relative rounded-control border-2 border-primary/30 bg-primary-soft/30 p-3.5 flex flex-col justify-between shadow-2xs">
                <div className="flex items-center justify-between text-caption text-primary">
                  <span className="font-semibold text-primary-fg truncate">Hoàn tất</span>
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-primary">98</span>
                  <span className="text-caption text-primary/80">đã xong</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-primary/20 text-[11px]">
                  <span className="text-primary-fg/80 truncate">100% đạt QC</span>
                  <span className="px-1.5 py-0.5 rounded bg-primary-soft text-primary-fg font-semibold border border-primary/25">
                    Primary Soft
                  </span>
                </div>
              </div>

              {/* Card 5: Warning - Orange Soft */}
              <div className="relative rounded-control border-2 border-warning/30 bg-warning-soft/30 p-3.5 flex flex-col justify-between shadow-2xs">
                <div className="flex items-center justify-between text-caption text-warning-fg">
                  <span className="font-semibold text-warning-fg truncate">Cảnh báo tồn</span>
                  <AlertTriangle className="size-3.5 text-warning-fg shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-warning-fg">7</span>
                  <span className="text-caption text-warning-fg/80">cần xử lý</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-warning/20 text-[11px]">
                  <span className="text-warning-fg/80 truncate">Hạn &lt; 48h</span>
                  <span className="px-1.5 py-0.5 rounded bg-warning-soft text-warning-fg font-semibold border border-warning/25">
                    Cam Warning Soft
                  </span>
                </div>
              </div>

              {/* Card 6: Danger - Red Soft */}
              <div className="relative rounded-control border-2 border-danger/30 bg-danger-soft/30 p-3.5 flex flex-col justify-between shadow-2xs">
                <div className="flex items-center justify-between text-caption text-danger-fg">
                  <span className="font-semibold text-danger-fg truncate">Đã hủy / HOLD</span>
                  <XCircle className="size-3.5 text-danger-fg shrink-0" />
                </div>
                <div className="my-1.5 flex items-baseline gap-1.5">
                  <span className="text-h1 font-bold text-danger-fg">4</span>
                  <span className="text-caption text-danger-fg/80">hủy/giữ</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t border-danger/20 text-[11px]">
                  <span className="text-danger-fg/80 truncate">3 hủy, 1 giữ</span>
                  <span className="px-1.5 py-0.5 rounded bg-danger-soft text-danger-fg font-semibold border border-danger/25">
                    Đỏ Danger Soft
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 2. MÔ PHỎNG TIẾN TRÌNH STEPPER & TIMELINE ĐỒNG BỘ */}
      <div className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-4">
        <div className="border-b border-border/70 pb-3">
          <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
            <Activity className="size-5 text-primary" />
            <span>Mô Phỏng Thực Tế 2: Thanh Tiến Trình (StatusStepper) & Dòng Thời Gian (Timeline)</span>
          </h3>
          <p className="m-0 text-caption text-text-muted mt-0.5">
            Mốc đang chạy có spinner xoay tròn và màu xanh dương nổi bật; mốc xong màu xanh primary; mốc chờ màu xám; mốc dừng/lỗi màu đỏ.
          </p>
        </div>

        {/* Demo Stepper */}
        <div className="rounded-lg border border-border/80 bg-bg/40 p-4">
          <div className="text-caption font-bold text-text-secondary uppercase tracking-wider mb-4">
            Quy trình thực tế: Tiếp nhận đơn → Lập kế hoạch → Chế biến (Đang chạy) → KCS Kiểm tra → Hoàn tất
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {/* Step 1: Hoàn tất */}
            <div className="flex md:flex-col items-center gap-3 text-left md:text-center p-2 rounded-lg bg-surface border border-primary/20">
              <div className="flex size-9 items-center justify-center rounded-full bg-primary text-on-primary ring-2 ring-primary/25 shadow-xs shrink-0">
                <Check className="size-5 stroke-[2.5]" />
              </div>
              <div className="min-w-0">
                <div className="text-caption font-bold text-text">1. Tiếp nhận đơn</div>
                <div className="text-[11px] text-primary-fg font-medium">Đã xác nhận</div>
                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-primary-soft text-primary-fg font-semibold">
                  Hoàn tất (Green)
                </span>
              </div>
            </div>

            {/* Step 2: Hoàn tất */}
            <div className="flex md:flex-col items-center gap-3 text-left md:text-center p-2 rounded-lg bg-surface border border-primary/20">
              <div className="flex size-9 items-center justify-center rounded-full bg-primary text-on-primary ring-2 ring-primary/25 shadow-xs shrink-0">
                <Check className="size-5 stroke-[2.5]" />
              </div>
              <div className="min-w-0">
                <div className="text-caption font-bold text-text">2. Phân bổ NVL</div>
                <div className="text-[11px] text-primary-fg font-medium">Đã xuất kho đệm</div>
                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-primary-soft text-primary-fg font-semibold">
                  Hoàn tất (Green)
                </span>
              </div>
            </div>

            {/* Step 3: Đang chạy (Active / Running) */}
            <div className="flex md:flex-col items-center gap-3 text-left md:text-center p-2 rounded-lg bg-blue-50 border-2 border-blue-400 ring-2 ring-blue-500/20 shadow-xs">
              <div className="flex size-9 items-center justify-center rounded-full bg-blue-600 text-white ring-4 ring-blue-500/25 shadow-xs shrink-0">
                <Loader2 className="size-5 animate-spin" />
              </div>
              <div className="min-w-0">
                <div className="text-caption font-bold text-blue-800">3. Chế biến IQF</div>
                <div className="text-[11px] text-blue-700 font-semibold">Dây chuyền 01</div>
                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white font-bold animate-pulse">
                  Đang chạy (Blue)
                </span>
              </div>
            </div>

            {/* Step 4: Chờ (Pending) */}
            <div className="flex md:flex-col items-center gap-3 text-left md:text-center p-2 rounded-lg bg-surface border border-border/70 opacity-75">
              <div className="flex size-9 items-center justify-center rounded-full bg-surface border border-border text-text-muted shrink-0 text-caption font-bold">
                4
              </div>
              <div className="min-w-0">
                <div className="text-caption font-semibold text-text-muted">4. Kiểm định KCS</div>
                <div className="text-[11px] text-text-muted">Chờ mẻ hoàn tất</div>
                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300 font-medium">
                  Chờ duyệt (Draft)
                </span>
              </div>
            </div>

            {/* Step 5: Chờ (Pending) */}
            <div className="flex md:flex-col items-center gap-3 text-left md:text-center p-2 rounded-lg bg-surface border border-border/70 opacity-75">
              <div className="flex size-9 items-center justify-center rounded-full bg-surface border border-border text-text-muted shrink-0 text-caption font-bold">
                5
              </div>
              <div className="min-w-0">
                <div className="text-caption font-semibold text-text-muted">5. Nhập kho thành phẩm</div>
                <div className="text-[11px] text-text-muted">Chờ đóng thùng</div>
                <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300 font-medium">
                  Chờ sx (Draft)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. CHI TIẾT BẢNG MA TRẬN 6 NHÓM MÀU (COMPREHENSIVE MATRIX TABLE) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="m-0 text-h3 font-bold text-text">
            Bảng Chi Tiết 6 Nhóm Màu & Định Dạng Áp Dụng Toàn Diện
          </h3>
          <span className="text-caption text-text-muted">
            Hiển thị {filteredGroups.length} / 6 nhóm màu
          </span>
        </div>

        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <div
              key={group.id}
              className={cn(
                "rounded-control border-2 bg-surface p-5 shadow-xs transition-all",
                group.id === "total" && "border-slate-500 bg-slate-50/50",
                group.id === "draft" && "border-slate-300 bg-slate-100/30",
                group.id === "running" && "border-info/40 bg-info-soft/10",
                group.id === "completed" && "border-primary/40 bg-primary-soft/10",
                group.id === "warning" && "border-warning/40 bg-warning-soft/10",
                group.id === "danger" && "border-danger/40 bg-danger-soft/10"
              )}
            >
              {/* Header của từng nhóm */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-3">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "size-3 rounded-full shrink-0",
                        group.id === "total" && "bg-slate-700",
                        group.id === "draft" && "bg-slate-400 border border-slate-300",
                        group.id === "running" && "bg-info ring-2 ring-info/40",
                        group.id === "completed" && "bg-primary",
                        group.id === "warning" && "bg-warning",
                        group.id === "danger" && "bg-danger"
                      )}
                    />
                    <h4 className="m-0 text-h3 font-bold text-text">{group.name}</h4>
                  </div>
                  <p className="m-0 text-caption font-semibold text-text-secondary">
                    {group.ruleTitle}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] px-2 py-0.5 rounded bg-bg border border-border text-text-secondary font-mono">
                    {group.paletteClass.hexOrOklch}
                  </code>
                </div>
              </div>

              {/* Mô tả & Từ khóa */}
              <div className="py-3 text-body text-text-secondary">
                <p className="m-0 text-caption text-text-secondary leading-relaxed">
                  {group.description}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-semibold text-text-muted">Từ khóa nhận diện tự động:</span>
                  {group.keywords.map((kw) => (
                    <span
                      key={kw}
                      className="text-[11px] px-1.5 py-0.2 rounded bg-bg border border-border/60 text-text-secondary font-mono"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>

              {/* 3 Cột Showcase linh kiện đồng bộ */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border/50">
                {/* Cột 1: Thẻ Card KPI */}
                <div className="rounded-md border border-border/70 bg-bg/50 p-3 space-y-2">
                  <div className="flex items-center justify-between text-[11px] font-bold text-text-muted uppercase tracking-wider">
                    <span>Thẻ Card KPI</span>
                    <span className="text-[10px] text-primary font-bold">
                      {group.id === "total"
                        ? "Web Dashboard Neutral"
                        : group.id === "draft"
                          ? "Nền Xám Nhạt (Slate 100)"
                          : "Solid + White Text"}
                    </span>
                  </div>
                  {/* Thẻ Card */}
                  <div
                    className={cn(
                      "rounded-control p-3 shadow-xs",
                      group.id === "total" && "border border-slate-200 bg-white text-slate-900 shadow-xs",
                      group.id === "draft" && "border border-slate-300 bg-slate-100 text-slate-800",
                      group.id === "running" && "border-2 border-info-fg/30 bg-info text-white ring-2 ring-info/30",
                      group.id === "completed" && "border-2 border-emerald-700 bg-primary text-white ring-2 ring-primary/40",
                      group.id === "warning" && "border-2 border-warning-fg/30 bg-warning text-white ring-2 ring-warning/30",
                      group.id === "danger" && "border-2 border-danger-fg/30 bg-danger text-white ring-2 ring-danger/30"
                    )}
                  >
                    <div className="flex items-center justify-between text-caption">
                      <span className={cn("font-semibold truncate", group.id === "total" ? "text-slate-500 font-medium" : group.id === "draft" ? "text-slate-700" : "text-white")}>
                        {group.kpiDemo.label}
                      </span>
                      {group.id === "running" ? (
                        <Loader2 className="size-3.5 text-white animate-spin" />
                      ) : group.id === "completed" ? (
                        <CheckCircle2 className="size-3.5 text-white" />
                      ) : group.id === "warning" ? (
                        <AlertTriangle className="size-3.5 text-white" />
                      ) : group.id === "danger" ? (
                        <XCircle className="size-3.5 text-white" />
                      ) : group.id === "total" ? (
                        <Layers className="size-3.5 text-sky-600" />
                      ) : (
                        <FileText className="size-3.5 text-slate-600" />
                      )}
                    </div>
                    <div className="my-1 flex items-baseline gap-1">
                      <span className={cn("text-h2 font-black", group.id === "total" || group.id === "draft" ? "text-slate-900" : "text-white")}>
                        {group.kpiDemo.value}
                      </span>
                      <span className={cn("text-[11px]", group.id === "total" ? "text-slate-500" : group.id === "draft" ? "text-slate-500" : "text-white/80")}>mục</span>
                    </div>
                    <div className={cn("text-[11px] truncate", group.id === "total" ? "text-slate-500" : group.id === "draft" ? "text-slate-600" : "text-white/80")}>
                      {group.kpiDemo.hint}
                    </div>
                  </div>
                </div>

                {/* Cột 2: Các mẫu StatusBadge thực tế */}
                <div className="rounded-md border border-border/70 bg-bg/50 p-3 space-y-2">
                  <div className="flex items-center justify-between text-[11px] font-bold text-text-muted uppercase tracking-wider">
                    <span>Nhãn StatusBadge</span>
                    <span className="text-[10px] text-text-secondary">
                      {group.id === "total"
                        ? "Web Dashboard Neutral"
                        : group.id === "draft"
                          ? "Nền Xám Nhạt (Light Gray)"
                          : "Solid & Soft"}
                    </span>
                  </div>
                  {/* Nhãn Nhóm Màu Xám / Solid */}
                  <div className="space-y-1">
                    <span className="text-[10px] text-text-muted font-semibold block">
                      {group.id === "total"
                        ? "Màu trung tính Web Dashboard (All, total, tất cả, tổng):"
                        : group.id === "draft"
                          ? "Màu nền nhạt của màu xám (draft, nháp, pending, chờ duyệt, chờ sx, tạm lưu):"
                          : "Kiểu 1: Màu gốc chữ trắng (Solid):"}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {group.sampleBadges.map((badgeName) => (
                        <span
                          key={`badge-sample-${badgeName}`}
                          className={cn(
                            "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-pill text-[11px] font-medium shadow-2xs leading-tight",
                            group.id === "total" && "bg-white text-slate-700 border border-slate-200",
                            group.id === "draft" && "bg-slate-100 text-slate-700 border border-slate-300",
                            group.id === "running" && "bg-info text-white border border-info-fg/30",
                            group.id === "completed" && "bg-primary text-white border border-emerald-700",
                            group.id === "warning" && "bg-warning text-white border border-warning-fg/30",
                            group.id === "danger" && "bg-danger text-white border border-danger-fg/30"
                          )}
                        >
                          {group.id === "running" && <Loader2 className="size-3 animate-spin text-white" />}
                          {group.id === "completed" && <CheckCircle2 className="size-3 text-white" />}
                          {group.id === "warning" && <AlertTriangle className="size-3 text-white" />}
                          {group.id === "danger" && <XCircle className="size-3 text-white" />}
                          <span>{badgeName}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Nhãn Soft (% Nhạt - Đối chiếu cho các nhóm khác) */}
                  {group.id !== "draft" && group.id !== "total" && (
                    <div className="space-y-1 pt-1 border-t border-border/40">
                      <span className="text-[10px] text-text-muted font-medium block">Kiểu 2: Nền nhạt % (Soft - Đối chiếu):</span>
                      <div className="flex flex-wrap gap-1.5">
                        {group.sampleBadges.map((badgeName) => (
                          <span
                            key={`soft-${badgeName}`}
                            className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.2 rounded-pill text-[11px] font-medium leading-tight",
                              group.id === "running" && "bg-blue-100 text-blue-700 border border-blue-300",
                              group.id === "completed" && "bg-primary-soft text-primary-fg border border-primary/25",
                              group.id === "warning" && "bg-warning-soft text-warning-fg border border-warning/25",
                              group.id === "danger" && "bg-danger-soft text-danger-fg border border-danger/25"
                            )}
                          >
                            <span>{badgeName}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Cột 3: Tiến trình / Timeline Action */}
                <div className="rounded-md border border-border/70 bg-bg/50 p-3 space-y-2">
                  <div className="text-[11px] font-bold text-text-muted uppercase tracking-wider">
                    Hiển Thị Trong Timeline & Tiến Trình
                  </div>
                  <div
                    className={cn(
                      "rounded-lg border p-2.5 space-y-1 text-caption",
                      group.id === "running"
                        ? "bg-blue-50/60 border-blue-200"
                        : "bg-surface border-border/70"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          "font-bold",
                          group.id === "running" ? "text-blue-800" : "text-text"
                        )}
                      >
                        {group.timelineDemo.action}
                      </span>
                      <span className="text-[10px] text-text-muted">
                        {group.timelineDemo.time}
                      </span>
                    </div>
                    <div className="text-[11px] text-text-muted">
                      Người thực hiện: <span className="text-text-secondary">{group.timelineDemo.user}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 4. TỔNG KẾT & KẾ HOẠCH TRIỂN KHAI TOÀN BỘ CODEBASE */}
      <div className="rounded-control border border-border bg-surface p-5 shadow-xs space-y-3">
        <h3 className="m-0 text-h3 font-bold text-text flex items-center gap-2">
          <CheckCircle2 className="size-5 text-primary" />
          <span>Kế Hoạch Triển Khai Đồng Bộ Mã Nguồn (Execution Plan)</span>
        </h3>
        <p className="m-0 text-caption text-text-muted">
          Sau khi bạn duyệt bảng tổng quan này, hệ thống sẽ thực hiện cập nhật triệt để theo 3 lớp:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
          <div className="rounded-lg border border-border/70 bg-bg p-3 space-y-1">
            <div className="text-caption font-bold text-text">Lớp 1: Core Mappings</div>
            <p className="m-0 text-[12px] text-text-secondary">
              Đã tách rõ <code>statusTone.ts</code> và <code>StatusBadge.tsx</code>: Tổng/All về <code>neutral</code> (Slate 700); draft/nháp/pending/chờ duyệt/chờ sx/tạm lưu về <code>draft</code> (Slate 100); Đang chạy về <code>info</code> (Màu xanh dương êm mắt); Hoàn tất về <code>primary</code>; Hủy/HOLD về <code>danger</code> (Màu đỏ êm mắt).
            </p>
          </div>

          <div className="rounded-lg border border-border/70 bg-bg p-3 space-y-1">
            <div className="text-caption font-bold text-text">Lớp 2: Component Primitives</div>
            <p className="m-0 text-[12px] text-text-secondary">
              Đồng bộ <code>KpiCard.tsx</code>, <code>Badge.tsx</code>, <code>StatusStepper.tsx</code>, <code>Timeline.tsx</code>, <code>FieldRoutingStepper.tsx</code> hiển thị đúng chuẩn màu và icon tương ứng.
            </p>
          </div>

          <div className="rounded-lg border border-border/70 bg-bg p-3 space-y-1">
            <div className="text-caption font-bold text-text">Lớp 3: Toàn Bộ Các Modules</div>
            <p className="m-0 text-[12px] text-text-secondary">
              Quét và chuẩn hóa toàn bộ các màn hình Danh sách, Chi tiết, Thống kê của các modules: <strong>MES</strong> (Sản xuất), <strong>Sales</strong> (Đơn bán), <strong>Warehouse</strong> (Kho bãi), <strong>QA/QC</strong> (Chất lượng).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestUiUnifiedStatusMatrix;
